// CActionIcon — 動作字樣提示（四家通用，含本家）
// 任一家（含本家）做出動作時，在「該家風字位置」以接近原尺寸（scale 0.93）顯示對應字樣：
//   - 吃 / 碰 / 槓 / 花：瞬時提示（Back.easeOut 彈入 → 淡出 → 銷毀）
//   - 聽：持續標（比照原版 mListenSprite — 放大進場後停留不淡出，直到不再聽牌 / 清除）
// 用來讓玩家一眼看到剛剛是誰、做了什麼動作。
//
// - 位置：共用 seat-badges 的 SEAT_BADGE_SPOTS（東南西北風字座標）
// - 瞬時提示：每家獨立播放；同一家連續觸發時排隊，避免槓後補花蓋掉「槓」字
// - 聽持續標：另一組獨立 sprite（不被吃/碰/槓/花的瞬時提示覆寫銷毀）
//
// === 使用圖片 ===
// - public/assets/ui/icons-action.png（key: ACTION_ICON_KEY，1×5 spritesheet 162×144/frame）
//     frame：0 吃 / 1 碰 / 2 槓 / 3 聽 / 4 花
// - 「槓」彈入時的閃光粒子透過 playCelebrationBurst 呈現，圖片清單見 src/ui/celebration-burst.ts 檔頭

import type Phaser from 'phaser';

import { MeldType, type TMeldType } from '@core/meld';
import type { TSeat } from '@core/types';
import { ACTION_ICON_KEY, ActionIconFrame, type TActionIconFrame } from '@scenes/boot-scene';

import { BurstSize, playCelebrationBurst } from './celebration-burst';
import { SEAT_BADGE_SPOTS } from './seat-badges';
import { UIDepth } from './ui-depths';

// ---- 版面 / 動畫常數 ----
const ICON_SCALE = 0.93; // 162×144 frame → 顯示約 151×134 px（原 0.62 的 1.5 倍）
const ICON_DEPTH = UIDepth.ActionIcon; // 高於牌面 / 徽章 / 回合手勢（70），確保浮在最上層
const ICON_Y_OFFSET = 0; // 圖示中心對齊風字徽章中心（直接疊在風字上）
const TRANSIENT_SCALE_IN_MS = 500; // 原版 ActionSprite：0 → 1，EaseBackOut
const FADE_OUT_MS = 500; // 原版 ActionSprite：彈入後淡出
const LISTEN_SCALE_IN_MS = 320; // 聽持續標：放大進場時長（Back.easeOut 彈入，比照原版 EaseBackOut）

/** 各 meld 類型對應的圖示 frame（各種槓都用「槓」字）。 */
const MELD_FRAME: Record<TMeldType, TActionIconFrame> = {
  [MeldType.Chi]: ActionIconFrame.Chi,
  [MeldType.Pong]: ActionIconFrame.Pong,
  [MeldType.KongExposed]: ActionIconFrame.Kong,
  [MeldType.KongConcealed]: ActionIconFrame.Kong,
  [MeldType.KongAdded]: ActionIconFrame.Kong,
};

/**
 * 別家動作字樣提示。每家最多同時一個提示，淡入淡出後自動銷毀。
 */
export class CActionIcon {
  private readonly sprites: Map<TSeat, Phaser.GameObjects.Image> = new Map();
  private readonly queues = new Map<
    TSeat,
    Array<{ readonly frame: TActionIconFrame; readonly onComplete?: () => void }>
  >();
  // 聽持續標：與瞬時提示分開管理，吃/碰/槓/花的瞬時提示不會把它銷毀
  private readonly listenSprites: Map<TSeat, Phaser.GameObjects.Image> = new Map();

  constructor(private readonly scene: Phaser.Scene) {}

  /** 在 seat 的風字位置彈入顯示該 meld 對應的動作字樣，完成後呼叫 onComplete。 */
  show(seat: TSeat, meld: TMeldType, onComplete?: () => void): void {
    this.enqueueTransient(seat, MELD_FRAME[meld], onComplete);
  }

  /** 摸到花：在該家風字位置淡入「花」字，停留後淡出（與吃/碰/槓提示同一套）。 */
  showFlower(seat: TSeat, onComplete?: () => void): void {
    this.enqueueTransient(seat, ActionIconFrame.Flower, onComplete);
  }

  /**
   * 設定哪些座位顯示持續「聽」標：新增的放大進場、移除的直接銷毀。
   * 換局 / 進出逐步模式時傳空集合即可清掉全部聽標。
   */
  setTenpaiSeats(seats: ReadonlySet<TSeat>): void {
    for (const seat of [...this.listenSprites.keys()]) {
      if (!seats.has(seat)) this.clearListen(seat);
    }
    for (const seat of seats) {
      if (this.listenSprites.has(seat)) continue;
      const spot = SEAT_BADGE_SPOTS[seat];
      const sprite = this.scene.add
        .image(spot.x, spot.y + ICON_Y_OFFSET, ACTION_ICON_KEY, ActionIconFrame.Listen)
        .setDepth(ICON_DEPTH)
        .setScale(0);
      this.listenSprites.set(seat, sprite);
      this.scene.tweens.add({
        targets: sprite,
        scaleX: ICON_SCALE,
        scaleY: ICON_SCALE,
        duration: LISTEN_SCALE_IN_MS,
        ease: 'Back.easeOut',
      });
    }
  }

  /** 同一家連續動作依序播放，確保「槓」完成後才輪到補花的「花」。 */
  private enqueueTransient(seat: TSeat, frame: TActionIconFrame, onComplete?: () => void): void {
    if (this.sprites.has(seat)) {
      const queue = this.queues.get(seat) ?? [];
      queue.push({ frame, ...(onComplete ? { onComplete } : {}) });
      this.queues.set(seat, queue);
      return;
    }
    this.playTransient(seat, frame, onComplete);
  }

  /** 原版 ActionSprite：由 0 彈入正常大小，再淡出銷毀。 */
  private playTransient(seat: TSeat, frame: TActionIconFrame, onComplete?: () => void): void {
    const spot = SEAT_BADGE_SPOTS[seat];
    const sprite = this.scene.add
      .image(spot.x, spot.y + ICON_Y_OFFSET, ACTION_ICON_KEY, frame)
      .setScale(0)
      .setDepth(ICON_DEPTH)
      .setAlpha(1);
    this.sprites.set(seat, sprite);
    if (frame === ActionIconFrame.Kong) {
      playCelebrationBurst(this.scene, spot.x, spot.y + ICON_Y_OFFSET, BurstSize.Small);
    }

    this.scene.tweens.add({
      targets: sprite,
      scaleX: ICON_SCALE,
      scaleY: ICON_SCALE,
      duration: TRANSIENT_SCALE_IN_MS,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: sprite,
          alpha: 0,
          duration: FADE_OUT_MS,
          ease: 'Quad.easeIn',
          onComplete: () => {
            sprite.destroy();
            if (this.sprites.get(seat) === sprite) this.sprites.delete(seat);
            onComplete?.();
            this.playNextTransient(seat);
          },
        });
      },
    });
  }

  private playNextTransient(seat: TSeat): void {
    const queue = this.queues.get(seat);
    const next = queue?.shift();
    if (!next) {
      this.queues.delete(seat);
      return;
    }
    if (queue?.length === 0) this.queues.delete(seat);
    this.playTransient(seat, next.frame, next.onComplete);
  }

  /** 立即清掉某家的瞬時提示（含進行中的 tween）。 */
  private clearSeat(seat: TSeat): void {
    const existing = this.sprites.get(seat);
    if (existing) {
      this.scene.tweens.killTweensOf(existing);
      existing.destroy();
      this.sprites.delete(seat);
    }
    this.queues.delete(seat);
  }

  /** 立即清掉某家的聽持續標。 */
  private clearListen(seat: TSeat): void {
    const existing = this.listenSprites.get(seat);
    if (existing) {
      this.scene.tweens.killTweensOf(existing);
      existing.destroy();
      this.listenSprites.delete(seat);
    }
  }

  /** 清掉全部提示 + 聽標（回合切換 / 場景重置用）。 */
  clear(): void {
    for (const seat of [...this.sprites.keys()]) this.clearSeat(seat);
    this.queues.clear();
    for (const seat of [...this.listenSprites.keys()]) this.clearListen(seat);
  }
}
